import { MouseEvent } from 'react'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import { toastEventDispatch } from '@space-lib-events/toast'
import Button from '@hotmart/space-lib/dist/components/button'
import { ApiError } from 'src/config/request'
import i18n from 'src/i18n'
import useDeletePost from 'src/hook/useDeletePost/useDeletePost'
import { useUser } from 'src/hook/useUser'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { CommunitiesEndpoints } from 'src/client/constants'
import {
  Actions,
  Entities,
  EventNames,
  ResultType,
  ResultsTypes,
  ScreenNames
} from 'src/utils/datahub/enums'
import { useCustomEvent } from 'src/hook/useCustomEvent'
import { FeedEventNames } from 'src/hook/usePosts'
import { PropsCommunity } from 'src/pages/CommunityDetails/Posts/types'
import { useManageFeedCache } from 'src/hook/useManageFeedCache'
import Modal from '../index'
import ModalHeader from '../header'
import ModalBody from '../body'
import ModalFooter from '../footer'

import { ButtonContainer } from './styles'
import { RemovePostProps } from './removePost.types'

const RemovePost = ({
  isOpen,
  idToRemove,
  isPinnedPost,
  isScheduledPosts,
  closeModal
}: RemovePostProps) => {
  const { mutate: mutateDeletePost, isLoading } = useDeletePost()
  const { user } = useUser()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { community } = useCommunityDetails()
  const { emitEvent } = useCustomEvent()
  const { onGetCacheArtifact, onUpdateCacheArtifact } = useManageFeedCache()

  const updateHeaderPostCount = () => {
    const queryKey = [CommunitiesEndpoints.getCommunity(community.id)]
    const cacheArtefact = onGetCacheArtifact<PropsCommunity>({ queryKey })

    if (cacheArtefact) {
      const [, data] = cacheArtefact
      const postCount = data?.sizeAcronym ? Number(data.sizeAcronym) - 1 : 0

      onUpdateCacheArtifact({
        queryKey,
        updatedData: {
          ...data,
          sizeAcronym: String(postCount)
        }
      })
    }
  }

  const handleSendEvent = (result: ResultType, failureKind = '') => {
    sendDataHubConsume({
      entity: isScheduledPosts ? Entities.SCHEDULED_POST : Entities.COMMUNITY,
      action: Actions.DELETE,
      event: {
        eventName: isScheduledPosts
          ? EventNames.TRY_TO_DELETE_SCHEDULE_POST
          : EventNames.TRY_TO_DELETE_A_POST,
        screenName: isScheduledPosts ? ScreenNames.SCHEDULED_ACTIONS : ScreenNames.POST_ACTIONS,
        user: {
          id: user.id,
          name: user.name
        },
        community: {
          id: community.id,
          name: community.name
        },
        result,
        failureKind,
        ...(isScheduledPosts && {})
      }
    })
  }

  const handleDeletePost = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    mutateDeletePost(idToRemove, {
      onSuccess() {
        emitEvent(FeedEventNames.POST_DELETED, {
          postId: idToRemove,
          isPinnedPost: Boolean(isPinnedPost),
          isScheduledPosts: Boolean(isScheduledPosts)
        })

        updateHeaderPostCount()

        if (isScheduledPosts) {
          handleSendEvent(ResultsTypes.SUCCESS)
        }

        closeModal()

        toastEventDispatch({
          type: ToastType.SUCCESS,
          message: i18n.t('notifications.delete_publication.success'),
          position: ToastPosition.BOTTOM_RIGHT
        })
      },
      onError(err) {
        handleSendEvent(ResultsTypes.FAILURE, (err as ApiError)?.message || '')
        toastEventDispatch({
          type: ToastType.DANGER,
          message: i18n.t('notifications.delete_publication.error'),
          position: ToastPosition.BOTTOM_RIGHT
        })
      }
    })
  }

  return (
    <Modal show={isOpen} onClose={closeModal}>
      <ModalHeader>
        <p className="_text-4 _mb-0">{i18n.t('remove_post.title')}</p>
      </ModalHeader>
      <ModalBody className="_py-0">
        <p className="_text-2 _text-gray-500 _mb-0">{i18n.t('remove_post.description')}</p>
      </ModalBody>
      <ModalFooter>
        <ButtonContainer className="_d-flex _w-full _justify-content-end _align-items-center">
          <Button variation="tertiary" onClickCapture={closeModal}>
            {i18n.t('buttons.cancel')}
          </Button>
          <Button
            disabled={isLoading}
            loading={isLoading}
            variation="primary"
            className="_ml-4"
            onClickCapture={handleDeletePost}
          >
            {i18n.t('buttons.remove')}
          </Button>
        </ButtonContainer>
      </ModalFooter>
    </Modal>
  )
}

export default RemovePost
